import React from "react";
import PropTypes from "prop-types";
import { Row, Col } from "react-flexbox-grid";
import { Table, Column, Cell } from "fixed-data-table-2";
import {
  AppBar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Toolbar,
  Typography
} from "@material-ui/core";
import moment from "moment";
import { getSoloPhysNum } from "util/solo";
import Close from "@material-ui/icons/Close";

const TIME_DIFF_WARN_S = 3 * 60;
const TIME_DIFF_ERR_S = 6 * 60;
const DEPTH_DIFF_WARN_M = 5;
const DEPTH_DIFF_ERR_M = 10;

const styles = {
  textInputStyle: {
    width: "100%",
    background: "transparent",
    border: "none"
  },
  warnDark: {
    backgroundColor: "hsl(40, 100%, 89%)"
  },
  warnLight: {
    backgroundColor: "hsl(40, 100%, 93%)"
  },
  errorDark: {
    backgroundColor: "hsl(359, 87%, 89%)"
  },
  errorLight: {
    backgroundColor: "hsl(359, 87%, 93%)"
  },
  tableFont: {
    fontFamily: "Roboto",
    fontSize: "14px"
  }
};

const getTimeCellStyle = (castTime, formTime, rowIndex) => {
  const timeDeltaMs = Math.abs(moment(castTime).diff(moment(formTime)));
  if (timeDeltaMs > TIME_DIFF_ERR_S * 1000) {
    return rowIndex % 2 === 0 ? styles.errorLight : styles.errorDark;
  }
  if (timeDeltaMs > TIME_DIFF_WARN_S * 1000) {
    return rowIndex % 2 === 0 ? styles.warnLight : styles.warnDark;
  }
  return {};
};

const getDepthCellStyle = (castDepth, formDepth, rowNum) => {
  const depthDelta = Math.abs(formDepth - castDepth);
  if (depthDelta > DEPTH_DIFF_ERR_M) {
    return rowNum % 2 === 0 ? styles.errorLight : styles.errorDark;
  }
  if (depthDelta > DEPTH_DIFF_WARN_M) {
    return rowNum % 2 === 0 ? styles.warnLight : styles.warnDark;
  }
  return {};
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AnnotationModal = props => (
  <Dialog
    fullScreen
    open={props.soloAnnotationModalOpen}
    onClose={props.onAnnotationModalClose}
    TransitionComponent={Transition}
  >
    <AppBar>
      <Toolbar>
        <IconButton onClick={props.onAnnotationModalClose} color="inherit">
          <Close />
        </IconButton>

        <Typography variant="h6" style={{ color: "#fff" }}>
          Change form
        </Typography>
      </Toolbar>
    </AppBar>

    <DialogTitle style={{ marginTop: 75, fontSize: 60 }}>
      Select the form that matches the instrument data
    </DialogTitle>
    <DialogContent>
      <Row center="xs" style={styles.tableFont}>
        <Col xs={12}>
          <h3>Instrument Data</h3>
        </Col>
        <Table
          rowsCount={1}
          rowHeight={30}
          headerHeight={30}
          width={1200}
          height={62}
        >
          <Column
            header="Date"
            cell={({ ...colProps }) => (
              <Cell {...colProps}>
                {moment(props.soloAnnotationModalCast.startDt).format("ll")}
              </Cell>
            )}
            width={110}
            flexGrow={1}
          />
          <Column
            header="Solo Number"
            cell={({ rowIndex, ...colProps }) => {
              const soloNum = getSoloPhysNum(
                props.soloAnnotationModalCast.deviceSn,
                props.soloUIDLookup
              );
              return (
                <Cell {...colProps} title={soloNum}>
                  {soloNum}
                </Cell>
              );
            }}
            width={90}
            flexGrow={1}
          />
          <Column
            header="Start"
            cell={({ ...colProps }) => {
              const dt = moment(props.soloAnnotationModalCast.startDt);
              return (
                <Cell
                  {...colProps}
                  title={props.soloAnnotationModalCast.startDt}
                >
                  {dt.format("LT")}
                </Cell>
              );
            }}
            width={77}
            flexGrow={1}
          />
          <Column
            header="Bottom"
            cell={({ ...colProps }) => {
              const dt = moment(props.soloAnnotationModalCast.bottomDt);
              return (
                <Cell
                  {...colProps}
                  title={props.soloAnnotationModalCast.bottomDt}
                >
                  {dt.format("LT")}
                </Cell>
              );
            }}
            width={77}
            flexGrow={1}
          />
          <Column
            header="Depth"
            cell={({ rowIndex, ...colProps }) => {
              const depth = props.soloAnnotationModalCast.bottomDepth
                ? `${Math.round(
                    props.soloAnnotationModalCast.bottomDepth * 10
                  ) / 10} m`
                : "-";
              return (
                <Cell {...colProps} title={depth}>
                  {depth}
                </Cell>
              );
            }}
            width={90}
            flexGrow={1}
          />
        </Table>
      </Row>

      <Row center="xs" style={{ ...styles.tableFont, marginTop: "2rem" }}>
        <Col xs={12}>
          <h3>Select the correct form:</h3>
        </Col>
        <Table
          rowsCount={props.soloAnnotationModalForms.length}
          rowHeight={30}
          headerHeight={30}
          width={1200}
          height={props.soloAnnotationModalForms.length * 30 + 32}
          onRowClick={props.onAnnotationRowSelect}
        >
          <Column
            cell={({ rowIndex, ...colProps }) => (
              <Cell {...colProps}>
                <input
                  type="radio"
                  checked={rowIndex === props.soloAnnotationModalSelected}
                  onChange={() => {}}
                />
              </Cell>
            )}
            width={35}
            flexGrow={1}
          />
          <Column
            header="Date"
            cell={({ rowIndex, ...colProps }) => {
              const form = props.soloAnnotationModalForms[rowIndex];
              const dt = moment(form.collected);
              return (
                <Cell {...colProps} title={form.collected}>
                  {dt.format("ll")}
                </Cell>
              );
            }}
            width={110}
            flexGrow={1}
          />
          <Column
            header="Site"
            cell={({ rowIndex, ...colProps }) => {
              const form = props.soloAnnotationModalForms[rowIndex];
              const { siteId } = form;
              return (
                <Cell {...colProps} title={siteId}>
                  {siteId}
                </Cell>
              );
            }}
            width={90}
            flexGrow={1}
          />
          <Column
            header="Drop"
            cell={({ rowIndex, ...colProps }) => {
              const form = props.soloAnnotationModalForms[rowIndex];
              const { drop } = form;
              return (
                <Cell {...colProps} title={drop}>
                  {drop}
                </Cell>
              );
            }}
            width={90}
            flexGrow={1}
          />
          <Column
            header="Time"
            cell={({ rowIndex, ...colProps }) => {
              const form = props.soloAnnotationModalForms[rowIndex];
              const dt = moment(form.collected);
              const style = getTimeCellStyle(
                props.soloAnnotationModalCast.startDt,
                form.collected,
                rowIndex
              );
              return (
                <Cell {...colProps} style={style} title={dt.format()}>
                  {dt.format("LT")}
                </Cell>
              );
            }}
            width={77}
            flexGrow={1}
          />
          <Column
            header="Target depth"
            cell={({ rowIndex, ...colProps }) => {
              const form = props.soloAnnotationModalForms[rowIndex];
              const style = getDepthCellStyle(
                props.soloAnnotationModalCast.bottomDepth,
                form.targetDepthM,
                rowIndex
              );
              return (
                <Cell
                  {...colProps}
                  style={style}
                >{`${form.targetDepthM}m`}</Cell>
              );
            }}
            width={100}
            flexGrow={1}
          />
        </Table>
      </Row>
    </DialogContent>
    <DialogActions>
      <Button variant="text" onClick={props.onAnnotationModalClose}>
        Close
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={props.onAnnotationUpdateClick}
        disabled={props.soloAnnotationModalSelected == null}
      >
        Update
      </Button>
    </DialogActions>
  </Dialog>
);

const formShape = PropTypes.shape({
  collected: PropTypes.string,
  targetDepthM: PropTypes.number,
  siteId: PropTypes.string,
  drop: PropTypes.number
});

const castShape = PropTypes.shape({
  soloFilePk: PropTypes.number,
  soloCastPk: PropTypes.number,
  startDt: PropTypes.string,
  bottomDt: PropTypes.string,
  bottomDepth: PropTypes.number,
  deviceSn: PropTypes.string,
  soloMatchingForm: formShape,
  possibleForms: PropTypes.arrayOf(formShape)
});

AnnotationModal.propTypes = {
  onAnnotationModalClose: PropTypes.func.isRequired,
  onAnnotationRowSelect: PropTypes.func.isRequired,
  onAnnotationUpdateClick: PropTypes.func.isRequired,
  soloAnnotationModalCast: castShape,
  soloAnnotationModalForms: PropTypes.arrayOf(formShape),
  soloAnnotationModalOpen: PropTypes.bool,
  soloAnnotationModalSelected: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  soloUIDLookup: PropTypes.object.isRequired
};

AnnotationModal.defaultProps = {
  soloAnnotationModalOpen: false,
  soloAnnotationModalCast: {},
  soloAnnotationModalForms: [],
  soloAnnotationModalSelected: null
};

export default AnnotationModal;
